import React, { useState } from 'react';
import { useRecordContext, useUpdate, useNotify, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import { CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { Funeral } from '../types';
import BurialPermitRequestModal from './BurialPermitRequestModal';

/**
 * Ontario's death-paperwork rows: Burial Permit and Coroner's Certificate.
 *
 * These sit in the same sidebar group as the DEC / Form 15 row and behave the
 * same way — a clickable label on the left, a status badge on the right that
 * opens a dialog. Ontario files these instead of the Québec DEC, so the group
 * reads as one workflow rather than two.
 */

type Tone = 'neutral' | 'progress' | 'done' | 'problem';

const TONE_CLASSES: Record<Tone, string> = {
  neutral: 'bg-gray-100 text-gray-500',
  progress: 'bg-amber-100 text-amber-700',
  done: 'bg-green-100 text-green-700',
  problem: 'bg-red-100 text-red-700',
};

/**
 * Multi-state sibling of SidebarStatusBadge. The burial permit has states the
 * shared boolean badge cannot express — a declined request is emphatically not
 * the same as one that was never sent.
 */
function StatusBadge({ label, tone }: { label: string; tone: Tone }) {
  const Icon = tone === 'done' ? CheckCircle2 : tone === 'problem' ? AlertCircle : Circle;
  const iconClass =
    tone === 'done'
      ? 'text-green-600'
      : tone === 'problem'
        ? 'text-red-600'
        : tone === 'progress'
          ? 'text-amber-500'
          : 'text-gray-300';

  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon className={`w-5 h-5 ${iconClass}`} aria-hidden="true" />
      <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${TONE_CLASSES[tone]}`}>
        {label}
      </span>
    </span>
  );
}

function Row({
  label,
  badge,
  onLabelClick,
  onBadgeClick,
}: {
  label: string;
  badge: React.ReactNode;
  onLabelClick: () => void;
  onBadgeClick: () => void;
}) {
  return (
    <div className="flex justify-between items-center py-1.5">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onLabelClick(); }}
        style={{ color: '#2563eb' }}
        className="text-[13px] hover:underline leading-5 shrink-0 focus:outline-none focus:ring-2 focus:ring-ring rounded text-left font-medium"
      >
        {label}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onBadgeClick(); }}
        className="inline-flex items-center gap-1.5 cursor-pointer rounded hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {badge}
      </button>
    </div>
  );
}

const BURIAL_PERMIT_STATES: Array<{ value: string; label: string; tone: Tone }> = [
  { value: 'not_requested', label: 'Not requested', tone: 'neutral' },
  { value: 'pending', label: 'Pending', tone: 'progress' },
  { value: 'issued', label: 'Issued', tone: 'done' },
  { value: 'declined', label: 'Declined', tone: 'problem' },
  { value: 'failed', label: 'Failed', tone: 'problem' },
];

const CORONER_STATES: Array<{ value: string; label: string; tone: Tone }> = [
  { value: 'not_requested', label: 'Not requested', tone: 'neutral' },
  { value: 'requested', label: 'Requested', tone: 'progress' },
  { value: 'received', label: 'Received', tone: 'done' },
];

/** Shared status-editing dialog. Writes straight to the funeral row. */
function StatusDialog({
  open,
  onClose,
  title,
  states,
  statusValue,
  numberValue,
  numberLabel,
  statusField,
  numberField,
  timestampField,
  doneValue,
  helper,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  states: Array<{ value: string; label: string }>;
  statusValue: string;
  numberValue: string;
  numberLabel: string;
  statusField: string;
  numberField: string;
  timestampField: string;
  doneValue: string;
  helper?: string;
}) {
  const record = useRecordContext<Funeral>();
  const [update] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [status, setStatus] = useState(statusValue);
  const [number, setNumber] = useState(numberValue);
  const [saving, setSaving] = useState(false);

  // Re-seed whenever the dialog opens so it never shows stale values.
  React.useEffect(() => {
    if (open) {
      setStatus(statusValue);
      setNumber(numberValue);
    }
  }, [open, statusValue, numberValue]);

  if (!record) return null;

  const handleSave = async () => {
    setSaving(true);
    const reachedDone = status === doneValue;
    const wasDone = (record as any)[statusField] === doneValue;
    try {
      await update(
        'funerals',
        {
          id: record.id,
          data: {
            ...record,
            [statusField]: status || null,
            [numberField]: number || null,
            // Stamp the first transition into the terminal state; clear it if
            // someone walks the status back.
            ...(reachedDone && !wasDone ? { [timestampField]: new Date().toISOString() } : {}),
            ...(!reachedDone && wasDone ? { [timestampField]: null } : {}),
          },
          previousData: record,
        },
        {
          mutationMode: 'pessimistic',
          onSuccess: () => {
            notify('Status updated', { type: 'success' });
            onClose();
            refresh();
          },
          onError: () => notify('Error updating status', { type: 'error' }),
        },
      );
    } catch {
      notify('Error updating status', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} onClick={(e) => e.stopPropagation()} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ minWidth: 280, my: 1 }}>
          {helper && (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          )}
          <TextField
            select
            fullWidth
            size="small"
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {states.map((s) => (
              <MenuItem key={s.value} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={numberLabel}
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Enter number"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => { e.stopPropagation(); onClose(); }} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={(e) => { e.stopPropagation(); void handleSave(); }}
          disabled={saving}
        >
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function OntarioPaperworkRows() {
  const record = useRecordContext<Funeral>();
  const refresh = useRefresh();
  const [requestOpen, setRequestOpen] = useState(false);
  const [permitStatusOpen, setPermitStatusOpen] = useState(false);
  const [coronerOpen, setCoronerOpen] = useState(false);

  if (!record) return null;
  // Ontario files the burial permit and coroner's certificate; Québec's
  // equivalent is the DEC, which already has its own row.
  if (((record.region as string) || '').toLowerCase() !== 'ontario') return null;

  const permitStatus = record.burial_permit_status || 'not_requested';
  const coronerStatus = record.coroner_certificate_status || 'not_requested';

  const permitState =
    BURIAL_PERMIT_STATES.find((s) => s.value === permitStatus) || BURIAL_PERMIT_STATES[0];
  const coronerState =
    CORONER_STATES.find((s) => s.value === coronerStatus) || CORONER_STATES[0];

  return (
    <>
      <Row
        label="Burial Permit"
        badge={<StatusBadge label={permitState.label} tone={permitState.tone} />}
        // The label starts the automated request; the badge records what
        // actually happened, including for permits filed by hand.
        onLabelClick={() => setRequestOpen(true)}
        onBadgeClick={() => setPermitStatusOpen(true)}
      />
      <Row
        label="Coroner Certificate"
        badge={<StatusBadge label={coronerState.label} tone={coronerState.tone} />}
        onLabelClick={() => setCoronerOpen(true)}
        onBadgeClick={() => setCoronerOpen(true)}
      />

      <BurialPermitRequestModal
        open={requestOpen}
        onClose={() => setRequestOpen(false)}
        funeralId={Number(record.id)}
        onSubmitted={() => refresh()}
      />

      <StatusDialog
        open={permitStatusOpen}
        onClose={() => setPermitStatusOpen(false)}
        title="Burial Permit"
        states={BURIAL_PERMIT_STATES}
        statusValue={permitStatus}
        numberValue={record.burial_permit_number || ''}
        numberLabel="Permit Number"
        statusField="burial_permit_status"
        numberField="burial_permit_number"
        timestampField="burial_permit_issued_at"
        doneValue="issued"
        helper="The bot keeps this current. Set it by hand for permits filed outside the app."
      />

      <StatusDialog
        open={coronerOpen}
        onClose={() => setCoronerOpen(false)}
        title="Coroner Certificate"
        states={CORONER_STATES}
        statusValue={coronerStatus}
        numberValue={record.coroner_certificate_number || ''}
        numberLabel="Coroner File Number"
        statusField="coroner_certificate_status"
        numberField="coroner_certificate_number"
        timestampField="coroner_certificate_received_at"
        doneValue="received"
        helper="Tracked by hand for now — the coroner's portal is a separate workflow."
      />
    </>
  );
}

export default OntarioPaperworkRows;
